import React, { useEffect, useRef, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Spinner } from '../ui.jsx';

// AI Coach — chat interface backed by the same agent loop the SMS/voice-note
// owner assistant uses. Owner can ask the coach anything: "give me my morning
// briefing", "who's overdue over $500", "text Sarah a reminder for Tuesday",
// "schedule Mike for the Smith install Thursday at 10am".

const QUICK_STARTS = [
  '📊 Give me my morning briefing',
  '💰 Who has overdue invoices over $500?',
  '📅 What\'s scheduled today?',
  '🚨 Show me at-risk customers',
  '👥 Search contacts named Smith',
  '🤖 What insights do you have for me?',
];

export function Coach({ toast }) {
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tradeops-coach-history') || '[]'); }
    catch { return []; }
  });
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem('tradeops-coach-history', JSON.stringify(messages.slice(-50))); } catch {}
    // Scroll to bottom
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || busy) return;
    const userMsg = { role: 'user', text: trimmed, at: Date.now() };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setBusy(true);
    try {
      const r = await api('/api/coach/chat', { method: 'POST', body: { message: trimmed }});
      setMessages(m => [...m, { role: 'assistant', text: r.reply || '(no reply)', at: Date.now() }]);
    } catch (e) {
      setMessages(m => [...m, { role: 'assistant', text: `Error: ${e.message}`, at: Date.now(), error: true }]);
      toast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    if (!confirm('Clear conversation history?')) return;
    setMessages([]);
    try { localStorage.removeItem('tradeops-coach-history'); } catch {}
  };

  return <div style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
    {/* Header */}
    <div style={{ padding: '12px 20px', borderBottom: `1px solid ${G.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>💎 AI Coach</div>
        <div style={{ fontSize: 12, color: G.textMuted, marginTop: 2 }}>
          Ask anything. Same brain works from this chat, SMS to your TradeOps number, or a voice note (MMS). Try "give me my morning briefing".
        </div>
      </div>
      <Btn sz="sm" v="ghost" onClick={reset}>↻ New conversation</Btn>
    </div>

    {/* Quick starts (only show when empty) */}
    {messages.length === 0 && (
      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 13, color: G.textMuted, marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>Quick starts</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {QUICK_STARTS.map(q => (
            <div key={q} onClick={() => send(q.replace(/^[^\s]+\s/, ''))}
              style={{ ...styles.card, padding: 14, cursor: 'pointer', fontSize: 14 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = G.purple}
              onMouseLeave={e => e.currentTarget.style.borderColor = G.border}>
              {q}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 24, fontSize: 12, color: G.textMuted, lineHeight: 1.6 }}>
          💡 <strong>Tip:</strong> Text your TradeOps business number from your owner phone — the same coach answers there. Send a voice note (MMS) and it transcribes + replies.
        </div>
      </div>
    )}

    {/* Message thread */}
    {messages.length > 0 && (
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {messages.map((m, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 12,
          }}>
            <div style={{
              maxWidth: '80%',
              padding: '12px 16px',
              borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
              background: m.role === 'user' ? G.purple : (m.error ? G.red + '22' : G.input),
              color: m.role === 'user' ? '#fff' : (m.error ? G.red : G.text),
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
            <div style={{ padding: '12px 16px', background: G.input, borderRadius: '16px 16px 16px 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Spinner sz={14}/> <span style={{ fontSize: 13, color: G.textMuted }}>Thinking…</span>
            </div>
          </div>
        )}
      </div>
    )}

    {/* Composer */}
    <div style={{ padding: 16, borderTop: `1px solid ${G.border}`, background: G.bg }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Ask the coach anything… (Enter to send, Shift+Enter for newline)"
          disabled={busy}
          style={{
            flex: 1,
            minHeight: 44,
            maxHeight: 200,
            padding: '12px 14px',
            background: G.input,
            border: `1px solid ${G.border}`,
            borderRadius: 10,
            color: G.text,
            fontFamily: 'inherit',
            fontSize: 14,
            resize: 'vertical',
            outline: 'none',
          }}
        />
        <Btn v="primary" onClick={() => send()} disabled={busy || !input.trim()}>
          {busy ? <Spinner sz={14}/> : 'Send →'}
        </Btn>
      </div>
    </div>
  </div>;
}
